import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '@mantine/core';
import checkoutService from '../services/checkout';
import { useTranslation } from '../context/TranslationContext';
import { useCart } from '../context/CartContext';
import './CheckoutSuccess.css';

export default function CheckoutSuccess() {
  const { t } = useTranslation();
  const { clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const orderId = searchParams.get('orderId');
  const type = searchParams.get('type');

  useEffect(() => {
    if (type === 'invoice' || type === 'monthly') {
      setStatus('success');
      return;
    }
    if (orderId) {
      confirmOrder();
    } else {
      setStatus('error');
    }
  }, [orderId, type]);

  const confirmOrder = async (retries = 10) => {
    try {
      const result = await checkoutService.confirmOrder(orderId);
      if (result.success) {
        setStatus('success');
        clearCart();
      } else if (retries > 0) {
        setTimeout(() => confirmOrder(retries - 1), 2000);
      } else {
        setStatus('error');
      }
    } catch (error) {
      if (retries > 0) {
        setTimeout(() => confirmOrder(retries - 1), 2000);
      } else {
        console.error('Order confirmation error:', error);
        setStatus('error');
      }
    }
  };

  if (status === 'processing') {
    return (
      <div className="checkout-success-page">
        <div className="loading-spinner" role="status" aria-live="polite">
          <div className="spinner" aria-hidden="true"></div>
          <p>{t('processingOrder', 'Processing your order…')}</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="checkout-success-page">
        <div className="error-state" role="alert">
          <h1 aria-hidden="true">❌</h1>
          <h2>{t('orderConfirmationFailed', 'Order Confirmation Failed')}</h2>
          <p>{t('orderConfirmationFailedText', "We couldn't confirm your order. Please contact support.")}</p>
          <Button component={Link} to="/contact">
            {t('contactSupport', 'Contact Support')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-success-page">
      <div className="success-state" role="status" aria-live="polite">
        <h1 aria-hidden="true">✅</h1>
        <h2>{t('orderConfirmed', 'Order Confirmed!')}</h2>
        {type === 'invoice'
          ? (t('invoiceSuccessText', 'Your invoice has been sent to your email. Please transfer the amount within 14 days.'))
          : (t('orderConfirmedText', 'Thank you for your order. You will receive a confirmation email shortly.'))}
        <p className="email-timing-note">{t('emailSentWithin', 'The confirmation email will be sent within the next 2 minutes.')}</p>
        <div className="success-actions">
          <Button component={Link} to="/products" variant="default">
            {t('continueShopping', 'Continue Shopping')}
          </Button>
        </div>
      </div>
    </div>
  );
}